/**
 * Shell-scope host (spec section 5.1), mounted in `AppShell` beside `ClientOptionsHost` so the draft
 * survives tool tab closes. While the panel is closed it renders nothing at all: the only feedback DOM
 * on any screen is the TopBar flag (this is what keeps existing e2e locators for `dialog`, `alert` and
 * `status` unambiguous). Everything that needs providers lives in `OpenFeedbackHost`, which only
 * mounts once the panel is open.
 */
import { useEffect } from 'react';
import { FeedbackPanel } from './FeedbackPanel';
import { FeedbackPill } from './FeedbackPill';
import { PickingBanner } from './PickingBanner';
import {
  closePanel,
  hasDraft,
  hydrateCorner,
  setModalOpen,
  useFeedbackState,
} from './feedbackStore';
import { useCanSubmitFeedback } from './useCanSubmitFeedback';
import { useFeedbackAvailability } from './useFeedbackAvailability';
import { useModalOpen } from './useModalOpen';
import { usePickController } from './usePickController';
import { useSendFeedback } from './useSendFeedback';

export function FeedbackHost() {
  const state = useFeedbackState();
  const request = state.focusRequest;

  // Focus moves are requested through the store so they survive the host unmounting (close -> flag).
  useEffect(() => {
    if (!request) return;
    if (request.onlyIfBody && document.activeElement && document.activeElement !== document.body) return;
    const target = document.querySelector<HTMLElement>(`[data-testid="${request.id}"]`);
    if (target) target.focus();
    else if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, [request]);

  if (state.view === 'closed') return null;
  return <OpenFeedbackHost />;
}

function OpenFeedbackHost() {
  const state = useFeedbackState();
  const canSubmit = useCanSubmitFeedback();
  const available = useFeedbackAvailability(canSubmit);
  const allowed = (canSubmit && available) || state.unavailable;
  const { startPick, pickMode } = usePickController(state.view === 'picking');
  const { send } = useSendFeedback();
  const draft = hasDraft(state);

  useEffect(() => {
    hydrateCorner();
  }, []);

  useModalOpen(true, setModalOpen);

  // A revoked permission or a turned-off environment tears the panel down.
  useEffect(() => {
    if (!allowed) closePanel();
  }, [allowed]);

  // While a draft exists, a reload or tab close asks first (X-12; keystroke isolation lets F5 reload).
  useEffect(() => {
    if (!draft) return undefined;
    const handler = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [draft]);

  if (!allowed) return null;

  if (state.view === 'minimized') {
    return <FeedbackPill corner={state.corner} paused={state.modalOpen} draft={draft} />;
  }
  return (
    <>
      <FeedbackPanel state={state} onPick={startPick} onSend={send} />
      {state.view === 'picking' && <PickingBanner corner={state.corner} mode={pickMode} />}
    </>
  );
}
